// Permission-to-route mapping for ui.finance.cg
// Derived from MENU_STRUCTURE as the single source of truth

import { MENU_STRUCTURE } from './menuStructure';

// Build a flat { permission: [paths] } map from MENU_STRUCTURE
function buildRoutePermissionMap() {
  const map = {};

  function walk(items, inheritedPermissions) {
    items.forEach(item => {
      const perms = item.permissions || inheritedPermissions || [];

      // Collect the item's own path
      if (item.path && perms.length > 0) {
        perms.forEach(perm => {
          if (!map[perm]) map[perm] = [];
          if (!map[perm].includes(item.path)) {
            map[perm].push(item.path);
          }
        });
      }

      // Collect subRoutes (inherit parent permissions)
      if (item.subRoutes) {
        item.subRoutes.forEach(sub => {
          if (sub.path && perms.length > 0) {
            perms.forEach(perm => {
              if (!map[perm]) map[perm] = [];
              if (!map[perm].includes(sub.path)) {
                map[perm].push(sub.path);
              }
            });
          }
        });
      }

      // Collect routes array
      if (item.routes) {
        item.routes.forEach(route => {
          if (route.path && perms.length > 0) {
            perms.forEach(perm => {
              if (!map[perm]) map[perm] = [];
              if (!map[perm].includes(route.path)) {
                map[perm].push(route.path);
              }
            });
          }
        });
      }

      // Recurse into children (they have their own permissions)
      if (item.children) {
        walk(item.children, null);
      }
    });
  }

  walk(MENU_STRUCTURE, null);
  return map;
}

// Lazily computed route-permission map
let _routePermissionMap = null;
function getRoutePermissionMap() {
  if (!_routePermissionMap) {
    _routePermissionMap = buildRoutePermissionMap();
  }
  return _routePermissionMap;
}

// Helper function to check if user has permission for a route
export const hasRoutePermission = (userPermissions, routePath) => {
  if (!userPermissions || !userPermissions.length) return false;

  const routeMap = getRoutePermissionMap();

  // Strip dynamic params (e.g. /edit/:id → /edit) for matching
  const normalizedPath = routePath.replace(/\/:[^/]+/g, '');

  for (const [permission, routes] of Object.entries(routeMap)) {
    for (const route of routes) {
      const normalizedRoute = route.replace(/\/:[^/]+/g, '');
      if (normalizedRoute === normalizedPath) {
        return userPermissions.includes(permission);
      }
    }
  }

  // If route is not in permission map, deny access
  return false;
};

// Helper function to filter menu structure based on user permissions
export const filterMenuByPermissions = (userPermissions) => {
  if (!userPermissions || !userPermissions.length) return [];

  const filterMenuItems = (menuItems) => {
    return menuItems.filter(menu => {
      // If the item itself has permissions, user must have at least one
      const hasPermission = menu.permissions && menu.permissions.length > 0;
      if (hasPermission) {
        const hasSelfPermission = !hasPermission || menu.permissions.some(permission => userPermissions.includes(permission));

        if (!hasSelfPermission) return false;

        // Check if menu item has any accessible children
        if (menu.children) {
          const accessibleChildren = filterMenuItems(menu.children);
          return accessibleChildren.length > 0;
        }

        // Leaf item with valid permissions
        return true;
      }
      
      // No permissions required, show it
      return true;
    }).map(menu => {
      if (menu.children) {
        // Filter children to only show accessible ones
        const accessibleChildren = filterMenuItems(menu.children);
        return { ...menu, children: accessibleChildren };
      }
      return menu;
    });
  };

  return filterMenuItems;
};
